import {Spec} from 'immutability-helper';

import {EntryVersion, FromServerId, ServerEntryVersion, ToServerId} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import EntryVersionUtil from 'notebook/util/EntryVersionUtil';
import {Version} from 'toolbar/shared/HistoryPanelComponent';
import pick from 'util/pick';
import {getWorkflowsBaseUrl} from 'workflows/util/WorkflowUtil';
import {ServerStageEntry, ServerStageEntryDocument, StageEntryDocument} from 'workflows/WorkflowConstants';

export function isStageEntryLocked(stageEntry: StageEntryDocument): boolean {
  const review = stageEntry.review;
  return Boolean(review && ReviewConstants.LOCKED_STATUSES.includes(review.status));
}

function _evUpdate<T extends StageEntryDocument | ServerStageEntryDocument>(
  stageEntry: T,
  fn: (entryVersion: T['entryVersion']) => EntryVersion
): StageEntryDocument {
  return {
    ...stageEntry,
    entryVersion: fn(stageEntry.entryVersion),
  } as StageEntryDocument;
}

export function entryVersionUpdate(op: Spec<EntryVersion>): Spec<StageEntryDocument> {
  return {entryVersion: op};
}

export function prepareForStore(
  stageEntry: ServerStageEntryDocument,
  cidIdMap: CidIdMap
): StageEntryDocument {
  return _evUpdate(stageEntry, (ev) => EntryVersionUtil.prepareForStore(ev, cidIdMap, stageEntry.id));
}

export function pickRevertableFields(stageEntry: StageEntryDocument): {entryVersion: EntryVersion} {
  return pick(stageEntry, 'entryVersion');
}

export function sanitizeStageEntry(
  stageEntry: StageEntryDocument,
  oldStageEntry: StageEntryDocument | null = null
): StageEntryDocument {
  return _evUpdate(stageEntry, (ev) =>
    EntryVersionUtil.sanitizeEntryVersion(ev, oldStageEntry && oldStageEntry.entryVersion, stageEntry.id)
  );
}

export function prepareForSave(
  stageEntry: StageEntryDocument,
  cidIdMap: CidIdMap
): {name: string; entryVersion: ServerEntryVersion<ToServerId>} {
  return {
    name: stageEntry.name,
    entryVersion: EntryVersionUtil.prepareForSave(stageEntry.entryVersion, cidIdMap, stageEntry.id),
  };
}

export function getEntryDocument(
  stageEntry: ServerStageEntry,
  entryVersion: ServerEntryVersion<FromServerId>
): ServerStageEntryDocument {
  return {
    ...stageEntry,
    entryVersion,
    latestVersionId: entryVersion.id,
  };
}

export function getPrintURL(entryId: string): string {
  return `${getWorkflowsBaseUrl()}/stage-entries/${entryId}/view`;
}

export function replaceEntryVersion(
  stageEntryDocument: StageEntryDocument,
  entryVersion: EntryVersion
): StageEntryDocument {
  return {
    ...stageEntryDocument,
    entryVersion,
  };
}

/**
 * Stage entry versions were detached from their entry at a few points in history.
 * We want to alert the user when the first tracked version is not actually the initial version.
 */
export function isFirstTrackedVersion(version: Version): boolean {
  // version.created_at is actually the created_at of the entry
  // The version creation time is stored in version.modified_at
  return !version.parentId && version.created_at !== version.modified_at;
}
